import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DuplicateMatch } from '@/types'

export type UploadStatus =
  | 'hashing'
  | 'checking'
  | 'conflict'
  | 'uploading'
  | 'completed'
  | 'failed'
  | 'skipped'

export interface UploadConflict {
  inTargetFolder: DuplicateMatch | null
  elsewhere: DuplicateMatch[]
}

export interface UploadItem {
  id: string
  name: string
  size: number
  mimeType: string
  folderId: number | null
  folderName: string | null
  status: UploadStatus
  progress: number
  error?: string
  checksum?: string
  conflict?: UploadConflict
  fileId?: number
  /** Aggregate rows (folder uploads) render a count instead of a size. */
  fileCount?: number
}

export interface PendingFolderNavigation {
  rootFolderId: number | null
  foldersCreated: number
  rootFolderName: string | null
}

interface UploadsState {
  items: UploadItem[]
  isPanelOpen: boolean
  isMinimized: boolean
  pendingFolderNavigation: PendingFolderNavigation | null
}

const initialState: UploadsState = {
  items: [],
  isPanelOpen: false,
  isMinimized: false,
  pendingFolderNavigation: null,
}

/** Statuses that mean the upload still needs something to happen. */
export const ACTIVE_STATUSES: UploadStatus[] = ['hashing', 'checking', 'conflict', 'uploading']

const patch = (state: UploadsState, id: string, changes: Partial<UploadItem>) => {
  const item = state.items.find((candidate) => candidate.id === id)
  if (item) {
    Object.assign(item, changes)
  }
}

const uploadsSlice = createSlice({
  name: 'uploads',
  initialState,
  reducers: {
    uploadsQueued: (state, action: PayloadAction<UploadItem[]>) => {
      state.items.push(...action.payload)
      state.isPanelOpen = true
      state.isMinimized = false
    },

    uploadHashed: (state, action: PayloadAction<{ id: string; checksum: string }>) => {
      patch(state, action.payload.id, {
        checksum: action.payload.checksum,
        status: 'checking',
      })
    },

    uploadConflicted: (state, action: PayloadAction<{ id: string; conflict: UploadConflict }>) => {
      patch(state, action.payload.id, {
        status: 'conflict',
        conflict: action.payload.conflict,
        progress: 0,
      })
    },

    uploadStarted: (state, action: PayloadAction<{ id: string }>) => {
      patch(state, action.payload.id, { status: 'uploading', progress: 0, error: undefined })
    },

    uploadProgress: (state, action: PayloadAction<{ id: string; progress: number }>) => {
      patch(state, action.payload.id, { progress: action.payload.progress })
    },

    uploadSucceeded: (state, action: PayloadAction<{ id: string; fileId?: number }>) => {
      patch(state, action.payload.id, {
        status: 'completed',
        progress: 100,
        fileId: action.payload.fileId,
        conflict: undefined,
      })
    },

    uploadFailed: (state, action: PayloadAction<{ id: string; error: string }>) => {
      patch(state, action.payload.id, { status: 'failed', error: action.payload.error })
    },

    uploadSkipped: (state, action: PayloadAction<{ id: string }>) => {
      patch(state, action.payload.id, { status: 'skipped', progress: 0 })
    },

    uploadRemoved: (state, action: PayloadAction<{ id: string }>) => {
      state.items = state.items.filter((item) => item.id !== action.payload.id)
      if (state.items.length === 0) {
        state.isPanelOpen = false
      }
    },

    clearFinishedUploads: (state) => {
      state.items = state.items.filter((item) => ACTIVE_STATUSES.includes(item.status))
      if (state.items.length === 0) {
        state.isPanelOpen = false
        state.isMinimized = false
      }
    },

    folderNavigationRequested: (state, action: PayloadAction<PendingFolderNavigation>) => {
      state.pendingFolderNavigation = action.payload
    },

    folderNavigationHandled: (state) => {
      state.pendingFolderNavigation = null
    },

    uploadPanelMinimizedToggled: (state) => {
      state.isMinimized = !state.isMinimized
    },

    uploadPanelClosed: (state) => {
      state.isPanelOpen = false
      state.isMinimized = false
      state.items = state.items.filter((item) => ACTIVE_STATUSES.includes(item.status))
    },
  },
})

export const {
  uploadsQueued,
  uploadHashed,
  uploadConflicted,
  uploadStarted,
  uploadProgress,
  uploadSucceeded,
  uploadFailed,
  uploadSkipped,
  uploadRemoved,
  clearFinishedUploads,
  folderNavigationRequested,
  folderNavigationHandled,
  uploadPanelMinimizedToggled,
  uploadPanelClosed,
} = uploadsSlice.actions

export default uploadsSlice.reducer
